// src/admin/components/FormConfig.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ref, get, set, remove, push } from "firebase/database";
import { db } from "../../lib/firebase";
import { CONFIG } from "../../config";
import "./FormConfig.css";

const uid = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const DEFAULT_WEB3FORMS_ACCESS_KEY = String(CONFIG.WEB3FORMS_ACCESS_KEY || "").trim();
const DEFAULT_FORM_NOTIFICATION_EMAIL = String(
  CONFIG.FORM_NOTIFICATION_EMAIL || ""
).trim();

const createEmptyForm = () => ({
  id: null,

  formName: "",
  formTitle: "Get Instant Quotes with No Hidden Fees",
  formDetails: "",

  // Page background
  bgType: "image", // "color" | "image" | "transparent"
  bgColor: "#0b0f19",
  bgImageUrl: "",
  bgImageFit: "cover", // cover | contain
  bgOverlay: true,
  bgOverlayColor: "rgba(0,0,0,0.55)",

  // Card container
  cardMaxWidth: 520,
  cardWidth: 520, // Actual width of the card
  cardHeight: "auto", // "auto" or number in px
  cardMinHeight: 0, // Minimum height
  cardPadding: 22,
  cardPaddingTop: 22,
  cardPaddingRight: 22,
  cardPaddingBottom: 22,
  cardPaddingLeft: 22,
  cardRadius: 14,
  cardBg: "#ffffff",
  cardBorderColor: "#e5e7eb",
  cardShadow: "0 24px 60px rgba(0,0,0,0.25)",
  cardAlign: "center", // left | center | right
  cardVerticalAlign: "center", // top | center | bottom

  // Typography - Title
  titleColor: "#111827",
  titleFontSize: 22,
  titleFontFamily: "inherit",
  titleFontWeight: 800,
  titleLetterSpacing: 0,
  titleLineHeight: 1.15,
  titleTextTransform: "none", // none | uppercase | lowercase | capitalize
  titleAlign: "left",
  titleMarginTop: 0,
  titleMarginRight: 0,
  titleMarginBottom: 10,
  titleMarginLeft: 0,
  titlePaddingTop: 0,
  titlePaddingRight: 0,
  titlePaddingBottom: 0,
  titlePaddingLeft: 0,
  
  // Typography - Description
  messageColor: "#4b5563",
  messageFontSize: 14,
  messageFontFamily: "inherit",
  messageFontWeight: 400,
  messageLetterSpacing: 0,
  messageLineHeight: 1.5,
  messageTextTransform: "none",
  messageAlign: "left",
  messageMarginTop: 0,
  messageMarginRight: 0,
  messageMarginBottom: 16,
  messageMarginLeft: 0,
  messagePaddingTop: 0,
  messagePaddingRight: 0,
  messagePaddingBottom: 0,
  messagePaddingLeft: 0,

  // Spacing
  fieldGap: 14,

  // Labels
  showLabels: false,
  labelColor: "#374151",
  labelFontSize: 13,
  labelFontWeight: 600,
  labelMarginBottom: 6,

  // Inputs (global)
  inputHeight: 44,
  inputRadius: 0,
  inputPaddingTop: 12,
  inputPaddingRight: 14,
  inputPaddingBottom: 12,
  inputPaddingLeft: 14,
  inputBorderColor: "#d1d5db",
  inputBg: "#ffffff",
  inputTextColor: "#111827",
  inputFontSize: 14,
  inputFontFamily: "inherit",
  inputFontWeight: 400,
  inputLetterSpacing: 0,
  inputLineHeight: 1.5,
  inputPlaceholderColor: "#9ca3af",
  inputPlaceholderFontSize: 14,
  inputPlaceholderFontWeight: 400,
  inputBorderWidth: 1,
  inputMarginTop: 0,
  inputMarginRight: 0,
  inputMarginBottom: 0,
  inputMarginLeft: 0,

  // Button - Text
  buttonText: "Submit",
  
  // Button - Colors
  submitButtonColor: "#c69a35",
  buttonTextColor: "#ffffff",
  buttonHoverColor: "#ffffff",
  buttonHoverBgColor: "#a67f20",
  
  // Button - Typography
  buttonFontSize: 14,
  buttonFontFamily: "inherit",
  buttonFontWeight: 700,
  buttonLetterSpacing: 0,
  buttonLineHeight: 1.5,
  
  // Button - Layout
  buttonRadius: 4,
  buttonPaddingTop: 12,
  buttonPaddingRight: 18,
  buttonPaddingBottom: 12,
  buttonPaddingLeft: 18,
  buttonAlign: "left", // left | center | right
  buttonWidth: "auto", // auto | full
  buttonMarginTop: 0,
  buttonMarginRight: 0,
  buttonMarginBottom: 0,
  buttonMarginLeft: 0,
  
  // Button - Size
  buttonHeight: 44,
  buttonMinWidth: 120,

  // Success message
  submitMessage: "Thank you! We will get back to you soon.",

  // Web3Forms Key
  accessKey: DEFAULT_WEB3FORMS_ACCESS_KEY,
  notificationEmail: DEFAULT_FORM_NOTIFICATION_EMAIL,

  // Fields
  fields: [
    { id: uid(), name: "Name", key: "name", type: "text", placeholder: "Name", required: true, options: [] },
    { id: uid(), name: "Email", key: "email", type: "email", placeholder: "Email", required: true, options: [] },
    { id: uid(), name: "Phone Number", key: "phone", type: "tel", placeholder: "Phone Number", required: true, options: [] },
    { id: uid(), name: "Project Name", key: "project", type: "text", placeholder: "Project Name", required: false, options: [] },
  ],
});

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "textarea", label: "Textarea" },
  { value: "select", label: "Select" },
];

const FONT_FAMILIES = [
  { value: "inherit", label: "Default" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "'Helvetica Neue', sans-serif", label: "Helvetica" },
  { value: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", label: "Segoe UI" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Open Sans', sans-serif", label: "Open Sans" },
  { value: "'Montserrat', sans-serif", label: "Montserrat" },
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "'Inter', sans-serif", label: "Inter" },
  { value: "'Georgia', serif", label: "Georgia" },
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "'Courier New', Courier, monospace", label: "Courier" },
];

const TEXT_TRANSFORM = [
  { value: "none", label: "Normal" },
  { value: "uppercase", label: "UPPERCASE" },
  { value: "lowercase", label: "lowercase" },
  { value: "capitalize", label: "Capitalize" },
];

const BUTTON_ALIGN = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

const BUTTON_WIDTH = [
  { value: "auto", label: "Auto" },
  { value: "full", label: "Full Width" },
];

export default function FormConfig() {
  const [forms, setForms] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    notifications: false,
    card: false,
    title: false,
    description: false,
    inputs: false,
    button: false,
    background: false,
    fields: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load from Firebase
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const snap = await get(ref(db, "formConfigs"));
        const val = snap.val();

        if (val) {
          const arr = Object.entries(val).map(([id, data]) => ({
            id,
            ...createEmptyForm(),
            ...data,
            // Backward compatibility
            cardWidth: data.cardWidth || data.cardMaxWidth || 520,
            cardHeight: data.cardHeight || "auto",
            cardMinHeight: data.cardMinHeight || 0,
            cardVerticalAlign: data.cardVerticalAlign || "center",
            cardPaddingTop: data.cardPaddingTop || data.cardPadding || 22,
            cardPaddingRight: data.cardPaddingRight || data.cardPadding || 22,
            cardPaddingBottom: data.cardPaddingBottom || data.cardPadding || 22,
            cardPaddingLeft: data.cardPaddingLeft || data.cardPadding || 22,
            
            titleFontFamily: data.titleFontFamily || "inherit",
            titleLetterSpacing: data.titleLetterSpacing || 0,
            titleTextTransform: data.titleTextTransform || "none",
            titlePaddingTop: data.titlePaddingTop || 0,
            titlePaddingRight: data.titlePaddingRight || 0,
            titlePaddingBottom: data.titlePaddingBottom || 0,
            titlePaddingLeft: data.titlePaddingLeft || 0,
            titleMarginRight: data.titleMarginRight || 0,
            titleMarginLeft: data.titleMarginLeft || 0,
            
            messageFontFamily: data.messageFontFamily || "inherit",
            messageLetterSpacing: data.messageLetterSpacing || 0,
            messageTextTransform: data.messageTextTransform || "none",
            messagePaddingTop: data.messagePaddingTop || 0,
            messagePaddingRight: data.messagePaddingRight || 0,
            messagePaddingBottom: data.messagePaddingBottom || 0,
            messagePaddingLeft: data.messagePaddingLeft || 0,
            messageMarginRight: data.messageMarginRight || 0,
            messageMarginLeft: data.messageMarginLeft || 0,
            
            inputPaddingTop: data.inputPaddingTop || 12,
            inputPaddingRight: data.inputPaddingRight || 14,
            inputPaddingBottom: data.inputPaddingBottom || 12,
            inputPaddingLeft: data.inputPaddingLeft || 14,
            inputFontFamily: data.inputFontFamily || "inherit",
            inputLetterSpacing: data.inputLetterSpacing || 0,
            inputMarginTop: data.inputMarginTop || 0,
            inputMarginRight: data.inputMarginRight || 0,
            inputMarginBottom: data.inputMarginBottom || 0,
            inputMarginLeft: data.inputMarginLeft || 0,
            inputPlaceholderFontSize: data.inputPlaceholderFontSize || 14,
            inputPlaceholderFontWeight: data.inputPlaceholderFontWeight || 400,
            
            buttonFontSize: data.buttonFontSize || 14,
            buttonFontFamily: data.buttonFontFamily || "inherit",
            buttonLetterSpacing: data.buttonLetterSpacing || 0,
            buttonPaddingTop: data.buttonPaddingTop || 12,
            buttonPaddingRight: data.buttonPaddingRight || 18,
            buttonPaddingBottom: data.buttonPaddingBottom || 12,
            buttonPaddingLeft: data.buttonPaddingLeft || 18,
            buttonMarginRight: data.buttonMarginRight || 0,
            buttonMarginLeft: data.buttonMarginLeft || 0,
            buttonHeight: data.buttonHeight || 44,
            buttonMinWidth: data.buttonMinWidth || 120,
            buttonWidth: data.buttonWidth || "auto",
            buttonHoverColor: data.buttonHoverColor || "#ffffff",
            buttonHoverBgColor: data.buttonHoverBgColor || "#a67f20",
            accessKey: String(data.accessKey || DEFAULT_WEB3FORMS_ACCESS_KEY).trim(),
            notificationEmail: String(
              data.notificationEmail || DEFAULT_FORM_NOTIFICATION_EMAIL
            ).trim(),
          }));
          setForms(arr);
        } else {
          setForms([createEmptyForm()]);
        }
        setActiveIndex(0);
      } catch (e) {
        console.error(e);
        setError("Failed to load forms.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const currentForm = forms[activeIndex] || null;

  const updateCurrentForm = (patch) => {
    setForms((prev) => {
      const copy = [...prev];
      copy[activeIndex] = { ...copy[activeIndex], ...patch };
      return copy;
    });
  };

  const handleInputChange = (field, value) => {
    updateCurrentForm({ [field]: value });
    setError("");
    setSuccess("");
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const normalizeField = (field) => {
    const base = {
      id: uid(),
      name: "",
      key: "",
      type: "text",
      placeholder: "",
      required: false,
      options: [],
    };
    return { ...base, ...field };
  };

  const handleFieldChange = (fieldIndex, key, value) => {
    setForms((prev) => {
      const copy = [...prev];
      const f = { ...copy[activeIndex] };
      const fields = [...(f.fields || [])].map(normalizeField);
      const updated = { ...fields[fieldIndex], [key]: value };

      if (key === "key") {
        updated.key = String(value || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");
      }

      if (key === "type" && value !== "select") {
        updated.options = [];
      }

      fields[fieldIndex] = updated;
      f.fields = fields;
      copy[activeIndex] = f;
      return copy;
    });
  };

  const addField = () => {
    setForms((prev) => {
      const copy = [...prev];
      const f = { ...copy[activeIndex] };
      const fields = [...(f.fields || [])].map(normalizeField);
      fields.push(normalizeField({ id: uid() }));
      f.fields = fields;
      copy[activeIndex] = f;
      return copy;
    });
  };

  const removeField = (i) => {
    setForms((prev) => {
      const copy = [...prev];
      const f = { ...copy[activeIndex] };
      const fields = [...(f.fields || [])].map(normalizeField);
      fields.splice(i, 1);
      f.fields = fields;
      copy[activeIndex] = f;
      return copy;
    });
  };

  const addNewForm = () => {
    setForms((prev) => [...prev, createEmptyForm()]);
    setActiveIndex(forms.length);
  };

  const deleteCurrentForm = async () => {
    if (!currentForm) return;

    if (!currentForm.id) {
      setForms([createEmptyForm()]);
      setActiveIndex(0);
      return;
    }

    try {
      await remove(ref(db, `formConfigs/${currentForm.id}`));
      setForms((prev) => prev.filter((_, i) => i !== activeIndex));
      setActiveIndex(0);
      setSuccess("Form deleted.");
    } catch (e) {
      console.error(e);
      setError("Failed to delete form.");
    }
  };

  const saveCurrentForm = async () => {
    if (!currentForm?.formName?.trim()) {
      setError("Form name is required.");
      return;
    }

    const keys = (currentForm.fields || [])
      .map((x) => String(x.key || "").trim())
      .filter(Boolean);
    const hasDup = new Set(keys).size !== keys.length;
    if (hasDup) {
      setError("Duplicate field keys found. Make each field key unique.");
      return;
    }

    setSaving(true);

    try {
      let id = currentForm.id;
      if (!id) id = push(ref(db, "formConfigs")).key;

      const payload = {
        formName: currentForm.formName,
        formTitle: currentForm.formTitle,
        formDetails: currentForm.formDetails,

        bgType: currentForm.bgType,
        bgColor: currentForm.bgColor,
        bgImageUrl: currentForm.bgImageUrl,
        bgImageFit: currentForm.bgImageFit,
        bgOverlay: currentForm.bgOverlay,
        bgOverlayColor: currentForm.bgOverlayColor,

        cardMaxWidth: Number(currentForm.cardWidth) || 520,
        cardWidth: Number(currentForm.cardWidth) || 520,
        cardHeight: currentForm.cardHeight === "auto" ? "auto" : Number(currentForm.cardHeight) || "auto",
        cardMinHeight: Number(currentForm.cardMinHeight) || 0,
        cardPadding: Number(currentForm.cardPadding) || 22,
        cardPaddingTop: Number(currentForm.cardPaddingTop) || 22,
        cardPaddingRight: Number(currentForm.cardPaddingRight) || 22,
        cardPaddingBottom: Number(currentForm.cardPaddingBottom) || 22,
        cardPaddingLeft: Number(currentForm.cardPaddingLeft) || 22,
        cardRadius: Number(currentForm.cardRadius) || 14,
        cardBg: currentForm.cardBg,
        cardBorderColor: currentForm.cardBorderColor,
        cardShadow: currentForm.cardShadow,
        cardAlign: currentForm.cardAlign,
        cardVerticalAlign: currentForm.cardVerticalAlign,

        titleColor: currentForm.titleColor,
        titleFontSize: Number(currentForm.titleFontSize) || 22,
        titleFontFamily: currentForm.titleFontFamily,
        titleFontWeight: Number(currentForm.titleFontWeight) || 800,
        titleLetterSpacing: Number(currentForm.titleLetterSpacing) || 0,
        titleLineHeight: Number(currentForm.titleLineHeight) || 1.15,
        titleTextTransform: currentForm.titleTextTransform,
        titleAlign: currentForm.titleAlign,
        titleMarginTop: Number(currentForm.titleMarginTop) || 0,
        titleMarginRight: Number(currentForm.titleMarginRight) || 0,
        titleMarginBottom: Number(currentForm.titleMarginBottom) || 10,
        titleMarginLeft: Number(currentForm.titleMarginLeft) || 0,
        titlePaddingTop: Number(currentForm.titlePaddingTop) || 0,
        titlePaddingRight: Number(currentForm.titlePaddingRight) || 0,
        titlePaddingBottom: Number(currentForm.titlePaddingBottom) || 0,
        titlePaddingLeft: Number(currentForm.titlePaddingLeft) || 0,

        messageColor: currentForm.messageColor,
        messageFontSize: Number(currentForm.messageFontSize) || 14,
        messageFontFamily: currentForm.messageFontFamily,
        messageFontWeight: Number(currentForm.messageFontWeight) || 400,
        messageLetterSpacing: Number(currentForm.messageLetterSpacing) || 0,
        messageLineHeight: Number(currentForm.messageLineHeight) || 1.5,
        messageTextTransform: currentForm.messageTextTransform,
        messageAlign: currentForm.messageAlign,
        messageMarginTop: Number(currentForm.messageMarginTop) || 0,
        messageMarginRight: Number(currentForm.messageMarginRight) || 0,
        messageMarginBottom: Number(currentForm.messageMarginBottom) || 16,
        messageMarginLeft: Number(currentForm.messageMarginLeft) || 0,
        messagePaddingTop: Number(currentForm.messagePaddingTop) || 0,
        messagePaddingRight: Number(currentForm.messagePaddingRight) || 0,
        messagePaddingBottom: Number(currentForm.messagePaddingBottom) || 0,
        messagePaddingLeft: Number(currentForm.messagePaddingLeft) || 0,

        fieldGap: Number(currentForm.fieldGap) || 14,

        showLabels: !!currentForm.showLabels,
        labelColor: currentForm.labelColor,
        labelFontSize: Number(currentForm.labelFontSize) || 13,
        labelFontWeight: Number(currentForm.labelFontWeight) || 600,
        labelMarginBottom: Number(currentForm.labelMarginBottom) || 6,

        inputHeight: Number(currentForm.inputHeight) || 44,
        inputRadius: Number(currentForm.inputRadius) || 0,
        inputPaddingTop: Number(currentForm.inputPaddingTop) || 12,
        inputPaddingRight: Number(currentForm.inputPaddingRight) || 14,
        inputPaddingBottom: Number(currentForm.inputPaddingBottom) || 12,
        inputPaddingLeft: Number(currentForm.inputPaddingLeft) || 14,
        inputBorderColor: currentForm.inputBorderColor,
        inputBg: currentForm.inputBg,
        inputTextColor: currentForm.inputTextColor,
        inputFontSize: Number(currentForm.inputFontSize) || 14,
        inputFontFamily: currentForm.inputFontFamily,
        inputFontWeight: Number(currentForm.inputFontWeight) || 400,
        inputLetterSpacing: Number(currentForm.inputLetterSpacing) || 0,
        inputLineHeight: Number(currentForm.inputLineHeight) || 1.5,
        inputPlaceholderColor: currentForm.inputPlaceholderColor,
        inputPlaceholderFontSize: Number(currentForm.inputPlaceholderFontSize) || 14,
        inputPlaceholderFontWeight: Number(currentForm.inputPlaceholderFontWeight) || 400,
        inputBorderWidth: Number(currentForm.inputBorderWidth) || 1,
        inputMarginTop: Number(currentForm.inputMarginTop) || 0,
        inputMarginRight: Number(currentForm.inputMarginRight) || 0,
        inputMarginBottom: Number(currentForm.inputMarginBottom) || 0,
        inputMarginLeft: Number(currentForm.inputMarginLeft) || 0,

        buttonText: currentForm.buttonText,
        submitButtonColor: currentForm.submitButtonColor,
        buttonTextColor: currentForm.buttonTextColor,
        buttonHoverColor: currentForm.buttonHoverColor,
        buttonHoverBgColor: currentForm.buttonHoverBgColor,
        buttonFontSize: Number(currentForm.buttonFontSize) || 14,
        buttonFontFamily: currentForm.buttonFontFamily,
        buttonFontWeight: Number(currentForm.buttonFontWeight) || 700,
        buttonLetterSpacing: Number(currentForm.buttonLetterSpacing) || 0,
        buttonLineHeight: Number(currentForm.buttonLineHeight) || 1.5,
        buttonRadius: Number(currentForm.buttonRadius) || 4,
        buttonPaddingTop: Number(currentForm.buttonPaddingTop) || 12,
        buttonPaddingRight: Number(currentForm.buttonPaddingRight) || 18,
        buttonPaddingBottom: Number(currentForm.buttonPaddingBottom) || 12,
        buttonPaddingLeft: Number(currentForm.buttonPaddingLeft) || 18,
        buttonAlign: currentForm.buttonAlign,
        buttonWidth: currentForm.buttonWidth,
        buttonHeight: Number(currentForm.buttonHeight) || 44,
        buttonMinWidth: Number(currentForm.buttonMinWidth) || 120,
        buttonMarginTop: Number(currentForm.buttonMarginTop) || 0,
        buttonMarginRight: Number(currentForm.buttonMarginRight) || 0,
        buttonMarginBottom: Number(currentForm.buttonMarginBottom) || 0,
        buttonMarginLeft: Number(currentForm.buttonMarginLeft) || 0,

        submitMessage: currentForm.submitMessage,
        accessKey: String(currentForm.accessKey || "").trim(),
        notificationEmail: String(currentForm.notificationEmail || "").trim(),

        fields: (currentForm.fields || []).map(normalizeField),
      };

      await set(ref(db, `formConfigs/${id}`), payload);
      setForms((prev) => {
        const copy = [...prev];
        copy[activeIndex] = { id, ...payload };
        return copy;
      });
      setSuccess("Form saved successfully!");
    } catch (e) {
      console.error(e);
      setError("Failed to save form.");
    } finally {
      setSaving(false);
    }
  };

  // ---------- PREVIEW ----------
  const preview = useMemo(() => {
    const form = currentForm;
    if (!form) return null;

    const outerStyle = {
      position: "relative",
      borderRadius: 14,
      overflow: "hidden",
      border: "1px solid #e5e7eb",
      height: "100%",
      minHeight: 400,
    };

    const bgStyle = {
      position: "absolute",
      inset: 0,
      background:
        form.bgType === "color"
          ? form.bgColor
          : form.bgType === "image" && form.bgImageUrl
          ? `url(${form.bgImageUrl}) center/${form.bgImageFit || "cover"} no-repeat`
          : "transparent",
    };

    const overlayStyle = {
      position: "absolute",
      inset: 0,
      background: form.bgOverlay ? form.bgOverlayColor : "transparent",
    };

    const cardWrapStyle = {
      position: "relative",
      zIndex: 1,
      height: "100%",
      display: "flex",
      justifyContent:
        form.cardAlign === "left"
          ? "flex-start"
          : form.cardAlign === "right"
          ? "flex-end"
          : "center",
      alignItems:
        form.cardVerticalAlign === "top"
          ? "flex-start"
          : form.cardVerticalAlign === "bottom"
          ? "flex-end"
          : "center",
      padding: 18,
    };

    const cardStyle = {
      width: "100%",
      maxWidth: Number(form.cardWidth) || 520,
      height: form.cardHeight === "auto" ? "auto" : `${Number(form.cardHeight)}px`,
      minHeight: form.cardHeight === "auto" ? `${Number(form.cardMinHeight) || 0}px` : "auto",
      background: form.cardBg,
      padding: `${Number(form.cardPaddingTop)}px ${Number(form.cardPaddingRight)}px ${Number(form.cardPaddingBottom)}px ${Number(form.cardPaddingLeft)}px`,
      borderRadius: Number(form.cardRadius) || 14,
      border: `1px solid ${form.cardBorderColor || "#e5e7eb"}`,
      boxShadow: form.cardShadow || "0 24px 60px rgba(0,0,0,0.25)",
      overflow: form.cardHeight !== "auto" ? "auto" : "visible",
    };

    const inputBase = {
      width: "100%",
      height: Number(form.inputHeight) || 44,
      borderRadius: Number(form.inputRadius) || 0,
      padding: `${Number(form.inputPaddingTop)}px ${Number(form.inputPaddingRight)}px ${Number(form.inputPaddingBottom)}px ${Number(form.inputPaddingLeft)}px`,
      border: `${Number(form.inputBorderWidth) || 1}px solid ${form.inputBorderColor || "#d1d5db"}`,
      background: form.inputBg || "#fff",
      color: form.inputTextColor || "#111827",
      outline: "none",
      fontFamily: form.inputFontFamily || "inherit",
      fontSize: `${Number(form.inputFontSize) || 14}px`,
      fontWeight: Number(form.inputFontWeight) || 400,
      letterSpacing: `${Number(form.inputLetterSpacing) || 0}px`,
      lineHeight: Number(form.inputLineHeight) || 1.5,
      margin: `${Number(form.inputMarginTop)}px ${Number(form.inputMarginRight)}px ${Number(form.inputMarginBottom)}px ${Number(form.inputMarginLeft)}px`,
    };

    const buttonContainerStyle = {
      display: "flex",
      justifyContent: form.buttonAlign === "left" ? "flex-start" : 
                    form.buttonAlign === "right" ? "flex-end" : "center",
      width: "100%",
    };

    const btnStyle = {
      width: form.buttonWidth === "full" ? "100%" : "auto",
      minWidth: Number(form.buttonMinWidth) || 120,
      height: Number(form.buttonHeight) || 44,
      border: "none",
      borderRadius: Number(form.buttonRadius) || 4,
      padding: `${Number(form.buttonPaddingTop)}px ${Number(form.buttonPaddingRight)}px ${Number(form.buttonPaddingBottom)}px ${Number(form.buttonPaddingLeft)}px`,
      background: form.submitButtonColor || "#111827",
      color: form.buttonTextColor || "#fff",
      fontFamily: form.buttonFontFamily || "inherit",
      fontSize: `${Number(form.buttonFontSize) || 14}px`,
      fontWeight: Number(form.buttonFontWeight) || 700,
      letterSpacing: `${Number(form.buttonLetterSpacing) || 0}px`,
      lineHeight: Number(form.buttonLineHeight) || 1.5,
      cursor: "pointer",
      margin: `${Number(form.buttonMarginTop)}px ${Number(form.buttonMarginRight)}px ${Number(form.buttonMarginBottom)}px ${Number(form.buttonMarginLeft)}px`,
      transition: "all 0.3s ease",
    };

    return (
      <div className="preview-container">
        <div className="preview-header">
          <h3>Live Preview</h3>
          <div className="preview-meta">
            <span>Card: {form.cardWidth}px</span>
            <span>Button: {form.buttonFontSize}px</span>
            <span>Align: {form.buttonAlign}</span>
          </div>
        </div>
        
        <div style={outerStyle}>
          <div style={bgStyle} />
          <div style={overlayStyle} />
          
          <div style={cardWrapStyle}>
            <div style={cardStyle}>
              {form.formTitle && (
                <h2
                  style={{
                    margin: 0,
                    marginTop: Number(form.titleMarginTop) || 0,
                    marginRight: Number(form.titleMarginRight) || 0,
                    marginBottom: Number(form.titleMarginBottom) || 10,
                    marginLeft: Number(form.titleMarginLeft) || 0,
                    paddingTop: Number(form.titlePaddingTop) || 0,
                    paddingRight: Number(form.titlePaddingRight) || 0,
                    paddingBottom: Number(form.titlePaddingBottom) || 0,
                    paddingLeft: Number(form.titlePaddingLeft) || 0,
                    color: form.titleColor,
                    fontSize: Number(form.titleFontSize) || 22,
                    fontFamily: form.titleFontFamily || "inherit",
                    fontWeight: Number(form.titleFontWeight) || 800,
                    letterSpacing: `${Number(form.titleLetterSpacing) || 0}px`,
                    lineHeight: Number(form.titleLineHeight) || 1.15,
                    textTransform: form.titleTextTransform,
                    textAlign: form.titleAlign || "left",
                  }}
                >
                  {form.formTitle}
                </h2>
              )}

              {form.formDetails && (
                <p
                  style={{
                    margin: 0,
                    marginTop: Number(form.messageMarginTop) || 0,
                    marginRight: Number(form.messageMarginRight) || 0,
                    marginBottom: Number(form.messageMarginBottom) || 16,
                    marginLeft: Number(form.messageMarginLeft) || 0,
                    paddingTop: Number(form.messagePaddingTop) || 0,
                    paddingRight: Number(form.messagePaddingRight) || 0,
                    paddingBottom: Number(form.messagePaddingBottom) || 0,
                    paddingLeft: Number(form.messagePaddingLeft) || 0,
                    fontSize: Number(form.messageFontSize) || 14,
                    fontFamily: form.messageFontFamily || "inherit",
                    fontWeight: Number(form.messageFontWeight) || 400,
                    letterSpacing: `${Number(form.messageLetterSpacing) || 0}px`,
                    lineHeight: Number(form.messageLineHeight) || 1.5,
                    color: form.messageColor,
                    textTransform: form.messageTextTransform,
                    textAlign: form.messageAlign || "left",
                  }}
                >
                  {form.formDetails}
                </p>
              )}

              <form style={{ display: "flex", flexDirection: "column", gap: Number(form.fieldGap) || 14 }}>
                {(form.fields || []).map((field) => {
                  const f = normalizeField(field);

                  return (
                    <div key={f.id}>
                      {form.showLabels && (
                        <label
                          style={{
                            display: "block",
                            marginBottom: Number(form.labelMarginBottom) || 6,
                            color: form.labelColor,
                            fontSize: Number(form.labelFontSize) || 13,
                            fontWeight: Number(form.labelFontWeight) || 600,
                          }}
                        >
                          {f.name}
                          {f.required ? " *" : ""}
                        </label>
                      )}

                      {f.type === "textarea" ? (
                        <textarea
                          disabled
                          placeholder={f.placeholder}
                          style={{
                            ...inputBase,
                            height: 96,
                            minHeight: 96,
                          }}
                        />
                      ) : f.type === "select" ? (
                        <select disabled style={{ ...inputBase }}>
                          <option value="">{f.placeholder || "Select"}</option>
                          {(f.options || []).map((opt, idx) => (
                            <option key={idx} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input 
                          disabled 
                          placeholder={f.placeholder} 
                          style={inputBase}
                        />
                      )}
                    </div>
                  );
                })}

                <div style={buttonContainerStyle}>
                  <button 
                    type="button" 
                    style={btnStyle}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = form.buttonHoverBgColor;
                      e.target.style.color = form.buttonHoverColor;
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = form.submitButtonColor;
                      e.target.style.color = form.buttonTextColor;
                    }}
                  >
                    {form.buttonText || "Submit"}
                  </button>
                </div>

                <div
                  style={{
                    marginTop: 6,
                    fontSize: Number(form.messageFontSize) || 14,
                    color: form.messageColor,
                  }}
                >
                  {form.submitMessage}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }, [currentForm]);

  if (loading) return <div className="loading">Loading forms...</div>;

  return (
    <div className="form-config-container">
      <div className="header">
        <h1>Form Builder</h1>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={addNewForm}>
            + New Form
          </button>
        </div>
      </div>

      <div className="main-layout">
        {/* Left Panel - Form List & Controls */}
        <div className="left-panel">
          {/* Form List */}
          <div className="form-list-section">
            <div className="section-header">
              <h3>Your Forms</h3>
            </div>
            <div className="form-list">
              {forms.map((f, i) => (
                <div
                  key={f.id || i}
                  className={`form-list-item ${i === activeIndex ? 'active' : ''}`}
                  onClick={() => setActiveIndex(i)}
                >
                  <div className="form-list-icon">📋</div>
                  <div className="form-list-content">
                    <div className="form-list-name">{f.formName || "Untitled Form"}</div>
                    <div className="form-list-desc">{f.formTitle || "No title"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="controls-container">
            {currentForm && (
              <>
                {/* Basic Info */}
                <div className="control-section">
                  <div className="section-header" onClick={() => toggleSection('basic')}>
                    <h3>Basic Info</h3>
                    <span className="toggle-icon">{expandedSections.basic ? '−' : '+'}</span>
                  </div>
                  {expandedSections.basic && (
                    <div className="section-content">
                      <div className="form-group">
                        <label>Form Name *</label>
                        <input
                          value={currentForm.formName}
                          onChange={(e) => handleInputChange("formName", e.target.value)}
                          placeholder="Contact Form"
                        />
                      </div>
                      <div className="form-group">
                        <label>Form Title</label>
                        <input
                          value={currentForm.formTitle}
                          onChange={(e) => handleInputChange("formTitle", e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          rows={2}
                          value={currentForm.formDetails}
                          onChange={(e) => handleInputChange("formDetails", e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Submission Notifications */}
                <div className="control-section">
                  <div
                    className="section-header"
                    onClick={() => toggleSection('notifications')}
                  >
                    <h3>Submission Notifications</h3>
                    <span className="toggle-icon">
                      {expandedSections.notifications ? '−' : '+'}
                    </span>
                  </div>
                  {expandedSections.notifications && (
                    <div className="section-content">
                      <div className="form-group">
                        <label>Success Message</label>
                        <input
                          value={currentForm.submitMessage}
                          onChange={(e) => handleInputChange("submitMessage", e.target.value)}
                          placeholder="Thank you! We will get back to you soon."
                        />
                      </div>
                      <div className="form-group">
                        <label>Web3Forms Access Key</label>
                        <input
                          value={currentForm.accessKey}
                          onChange={(e) => handleInputChange("accessKey", e.target.value)}
                          placeholder="Enter Web3Forms access key"
                        />
                      </div>
                      <div className="form-group">
                        <label>Notification Email (optional)</label>
                        <input
                          type="email"
                          value={currentForm.notificationEmail}
                          onChange={(e) =>
                            handleInputChange("notificationEmail", e.target.value)
                          }
                          placeholder="interiorregency6@gmail.com"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Button Styling */}
                <div className="control-section">
                  <div className="section-header" onClick={() => toggleSection('button')}>
                    <h3>Button Settings</h3>
                    <span className="toggle-icon">{expandedSections.button ? '−' : '+'}</span>
                  </div>
                  {expandedSections.button && (
                    <div className="section-content">
                      {/* Button Text */}
                      <div className="form-group">
                        <label>Button Text</label>
                        <input
                          value={currentForm.buttonText}
                          onChange={(e) => handleInputChange("buttonText", e.target.value)}
                          placeholder="Submit"
                        />
                      </div>

                      {/* Button Alignment */}
                      <div className="grid-2">
                        <div className="form-group">
                          <label>Alignment</label>
                          <select
                            value={currentForm.buttonAlign}
                            onChange={(e) => handleInputChange("buttonAlign", e.target.value)}
                          >
                            {BUTTON_ALIGN.map(align => (
                              <option key={align.value} value={align.value}>{align.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Width</label>
                          <select
                            value={currentForm.buttonWidth}
                            onChange={(e) => handleInputChange("buttonWidth", e.target.value)}
                          >
                            {BUTTON_WIDTH.map(width => (
                              <option key={width.value} value={width.value}>{width.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Button Colors */}
                      <div className="sub-section">
                        <h4>Colors</h4>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>Background Color</label>
                            <div className="color-input">
                              <input
                                type="color"
                                value={currentForm.submitButtonColor}
                                onChange={(e) => handleInputChange("submitButtonColor", e.target.value)}
                              />
                              <input
                                type="text"
                                value={currentForm.submitButtonColor}
                                onChange={(e) => handleInputChange("submitButtonColor", e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Text Color</label>
                            <div className="color-input">
                              <input
                                type="color"
                                value={currentForm.buttonTextColor}
                                onChange={(e) => handleInputChange("buttonTextColor", e.target.value)}
                              />
                              <input
                                type="text"
                                value={currentForm.buttonTextColor}
                                onChange={(e) => handleInputChange("buttonTextColor", e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>Hover Background</label>
                            <div className="color-input">
                              <input
                                type="color"
                                value={currentForm.buttonHoverBgColor}
                                onChange={(e) => handleInputChange("buttonHoverBgColor", e.target.value)}
                              />
                              <input
                                type="text"
                                value={currentForm.buttonHoverBgColor}
                                onChange={(e) => handleInputChange("buttonHoverBgColor", e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Hover Text Color</label>
                            <div className="color-input">
                              <input
                                type="color"
                                value={currentForm.buttonHoverColor}
                                onChange={(e) => handleInputChange("buttonHoverColor", e.target.value)}
                              />
                              <input
                                type="text"
                                value={currentForm.buttonHoverColor}
                                onChange={(e) => handleInputChange("buttonHoverColor", e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Button Typography */}
                      <div className="sub-section">
                        <h4>Typography</h4>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>Font Family</label>
                            <select
                              value={currentForm.buttonFontFamily}
                              onChange={(e) => handleInputChange("buttonFontFamily", e.target.value)}
                            >
                              {FONT_FAMILIES.map(font => (
                                <option key={font.value} value={font.value}>{font.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Font Size (px)</label>
                            <input
                              type="number"
                              value={currentForm.buttonFontSize}
                              onChange={(e) => handleInputChange("buttonFontSize", Number(e.target.value))}
                            />
                          </div>
                        </div>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>Font Weight</label>
                            <input
                              type="number"
                              value={currentForm.buttonFontWeight}
                              onChange={(e) => handleInputChange("buttonFontWeight", Number(e.target.value))}
                              min="100"
                              max="900"
                              step="100"
                            />
                          </div>
                          <div className="form-group">
                            <label>Letter Spacing (px)</label>
                            <input
                              type="number"
                              value={currentForm.buttonLetterSpacing}
                              onChange={(e) => handleInputChange("buttonLetterSpacing", Number(e.target.value))}
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Line Height</label>
                          <input
                            type="number"
                            step="0.1"
                            value={currentForm.buttonLineHeight}
                            onChange={(e) => handleInputChange("buttonLineHeight", Number(e.target.value))}
                          />
                        </div>
                      </div>

                      {/* Button Size & Spacing */}
                      <div className="sub-section">
                        <h4>Size & Spacing</h4>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>Height (px)</label>
                            <input
                              type="number"
                              value={currentForm.buttonHeight}
                              onChange={(e) => handleInputChange("buttonHeight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Min Width (px)</label>
                            <input
                              type="number"
                              value={currentForm.buttonMinWidth}
                              onChange={(e) => handleInputChange("buttonMinWidth", Number(e.target.value))}
                            />
                          </div>
                        </div>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>Border Radius (px)</label>
                            <input
                              type="number"
                              value={currentForm.buttonRadius}
                              onChange={(e) => handleInputChange("buttonRadius", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Button Padding */}
                      <div className="sub-section">
                        <h4>Padding</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.buttonPaddingTop}
                              onChange={(e) => handleInputChange("buttonPaddingTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.buttonPaddingRight}
                              onChange={(e) => handleInputChange("buttonPaddingRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.buttonPaddingBottom}
                              onChange={(e) => handleInputChange("buttonPaddingBottom", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.buttonPaddingLeft}
                              onChange={(e) => handleInputChange("buttonPaddingLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Button Margins */}
                      <div className="sub-section">
                        <h4>Margins</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.buttonMarginTop}
                              onChange={(e) => handleInputChange("buttonMarginTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.buttonMarginRight}
                              onChange={(e) => handleInputChange("buttonMarginRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.buttonMarginBottom}
                              onChange={(e) => handleInputChange("buttonMarginBottom", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.buttonMarginLeft}
                              onChange={(e) => handleInputChange("buttonMarginLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Settings */}
                <div className="control-section">
                  <div className="section-header" onClick={() => toggleSection('card')}>
                    <h3>Card Settings</h3>
                    <span className="toggle-icon">{expandedSections.card ? '−' : '+'}</span>
                  </div>
                  {expandedSections.card && (
                    <div className="section-content">
                      <div className="grid-2">
                        <div className="form-group">
                          <label>Width (px)</label>
                          <input
                            type="number"
                            value={currentForm.cardWidth}
                            onChange={(e) => handleInputChange("cardWidth", Number(e.target.value))}
                          />
                        </div>
                        <div className="form-group">
                          <label>Height</label>
                          <select
                            value={currentForm.cardHeight === "auto" ? "auto" : "fixed"}
                            onChange={(e) => handleInputChange("cardHeight", e.target.value === "auto" ? "auto" : 400)}
                          >
                            <option value="auto">Auto</option>
                            <option value="fixed">Fixed</option>
                          </select>
                        </div>
                      </div>
                      
                      {currentForm.cardHeight !== "auto" && (
                        <div className="form-group">
                          <label>Fixed Height (px)</label>
                          <input
                            type="number"
                            value={currentForm.cardHeight}
                            onChange={(e) => handleInputChange("cardHeight", Number(e.target.value))}
                          />
                        </div>
                      )}
                      
                      <div className="form-group">
                        <label>Card Background</label>
                        <div className="color-input">
                          <input
                            type="color"
                            value={currentForm.cardBg}
                            onChange={(e) => handleInputChange("cardBg", e.target.value)}
                          />
                          <input
                            type="text"
                            value={currentForm.cardBg}
                            onChange={(e) => handleInputChange("cardBg", e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="sub-section">
                        <h4>Padding</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.cardPaddingTop}
                              onChange={(e) => handleInputChange("cardPaddingTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.cardPaddingRight}
                              onChange={(e) => handleInputChange("cardPaddingRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.cardPaddingBottom}
                              onChange={(e) => handleInputChange("cardPaddingBottom", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.cardPaddingLeft}
                              onChange={(e) => handleInputChange("cardPaddingLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Title Styling */}
                <div className="control-section">
                  <div className="section-header" onClick={() => toggleSection('title')}>
                    <h3>Title Styling</h3>
                    <span className="toggle-icon">{expandedSections.title ? '−' : '+'}</span>
                  </div>
                  {expandedSections.title && (
                    <div className="section-content">
                      <div className="form-group">
                        <label>Text</label>
                        <input
                          value={currentForm.formTitle}
                          onChange={(e) => handleInputChange("formTitle", e.target.value)}
                        />
                      </div>
                      
                      <div className="grid-2">
                        <div className="form-group">
                          <label>Font Family</label>
                          <select
                            value={currentForm.titleFontFamily}
                            onChange={(e) => handleInputChange("titleFontFamily", e.target.value)}
                          >
                            {FONT_FAMILIES.map(font => (
                              <option key={font.value} value={font.value}>{font.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Font Size (px)</label>
                          <input
                            type="number"
                            value={currentForm.titleFontSize}
                            onChange={(e) => handleInputChange("titleFontSize", Number(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="grid-2">
                        <div className="form-group">
                          <label>Font Weight</label>
                          <input
                            type="number"
                            value={currentForm.titleFontWeight}
                            onChange={(e) => handleInputChange("titleFontWeight", Number(e.target.value))}
                          />
                        </div>
                        <div className="form-group">
                          <label>Text Transform</label>
                          <select
                            value={currentForm.titleTextTransform}
                            onChange={(e) => handleInputChange("titleTextTransform", e.target.value)}
                          >
                            {TEXT_TRANSFORM.map(transform => (
                              <option key={transform.value} value={transform.value}>{transform.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="sub-section">
                        <h4>Margins</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.titleMarginTop}
                              onChange={(e) => handleInputChange("titleMarginTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.titleMarginRight}
                              onChange={(e) => handleInputChange("titleMarginRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.titleMarginBottom}
                              onChange={(e) => handleInputChange("titleMarginBottom", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.titleMarginLeft}
                              onChange={(e) => handleInputChange("titleMarginLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="sub-section">
                        <h4>Padding</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.titlePaddingTop}
                              onChange={(e) => handleInputChange("titlePaddingTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.titlePaddingRight}
                              onChange={(e) => handleInputChange("titlePaddingRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.titlePaddingBottom}
                              onChange={(e) => handleInputChange("titlePaddingBottom", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.titlePaddingLeft}
                              onChange={(e) => handleInputChange("titlePaddingLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description Styling */}
                <div className="control-section">
                  <div className="section-header" onClick={() => toggleSection('description')}>
                    <h3>Description Styling</h3>
                    <span className="toggle-icon">{expandedSections.description ? '−' : '+'}</span>
                  </div>
                  {expandedSections.description && (
                    <div className="section-content">
                      <div className="grid-2">
                        <div className="form-group">
                          <label>Font Family</label>
                          <select
                            value={currentForm.messageFontFamily}
                            onChange={(e) => handleInputChange("messageFontFamily", e.target.value)}
                          >
                            {FONT_FAMILIES.map(font => (
                              <option key={font.value} value={font.value}>{font.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Font Size (px)</label>
                          <input
                            type="number"
                            value={currentForm.messageFontSize}
                            onChange={(e) => handleInputChange("messageFontSize", Number(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="grid-2">
                        <div className="form-group">
                          <label>Font Weight</label>
                          <input
                            type="number"
                            value={currentForm.messageFontWeight}
                            onChange={(e) => handleInputChange("messageFontWeight", Number(e.target.value))}
                          />
                        </div>
                        <div className="form-group">
                          <label>Text Transform</label>
                          <select
                            value={currentForm.messageTextTransform}
                            onChange={(e) => handleInputChange("messageTextTransform", e.target.value)}
                          >
                            {TEXT_TRANSFORM.map(transform => (
                              <option key={transform.value} value={transform.value}>{transform.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="sub-section">
                        <h4>Margins</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.messageMarginTop}
                              onChange={(e) => handleInputChange("messageMarginTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.messageMarginRight}
                              onChange={(e) => handleInputChange("messageMarginRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.messageMarginBottom}
                              onChange={(e) => handleInputChange("messageMarginBottom", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.messageMarginLeft}
                              onChange={(e) => handleInputChange("messageMarginLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Input Styling */}
                <div className="control-section">
                  <div className="section-header" onClick={() => toggleSection('inputs')}>
                    <h3>Input Styling</h3>
                    <span className="toggle-icon">{expandedSections.inputs ? '−' : '+'}</span>
                  </div>
                  {expandedSections.inputs && (
                    <div className="section-content">
                      <div className="grid-2">
                        <div className="form-group">
                          <label>Font Family</label>
                          <select
                            value={currentForm.inputFontFamily}
                            onChange={(e) => handleInputChange("inputFontFamily", e.target.value)}
                          >
                            {FONT_FAMILIES.map(font => (
                              <option key={font.value} value={font.value}>{font.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Font Size (px)</label>
                          <input
                            type="number"
                            value={currentForm.inputFontSize}
                            onChange={(e) => handleInputChange("inputFontSize", Number(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="grid-2">
                        <div className="form-group">
                          <label>Font Weight</label>
                          <input
                            type="number"
                            value={currentForm.inputFontWeight}
                            onChange={(e) => handleInputChange("inputFontWeight", Number(e.target.value))}
                          />
                        </div>
                        <div className="form-group">
                          <label>Placeholder Size</label>
                          <input
                            type="number"
                            value={currentForm.inputPlaceholderFontSize}
                            onChange={(e) => handleInputChange("inputPlaceholderFontSize", Number(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="sub-section">
                        <h4>Padding</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.inputPaddingTop}
                              onChange={(e) => handleInputChange("inputPaddingTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.inputPaddingRight}
                              onChange={(e) => handleInputChange("inputPaddingRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.inputPaddingBottom}
                              onChange={(e) => handleInputChange("inputPaddingBottom", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.inputPaddingLeft}
                              onChange={(e) => handleInputChange("inputPaddingLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="sub-section">
                        <h4>Margins</h4>
                        <div className="grid-4">
                          <div className="form-group">
                            <label>Top</label>
                            <input
                              type="number"
                              value={currentForm.inputMarginTop}
                              onChange={(e) => handleInputChange("inputMarginTop", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Right</label>
                            <input
                              type="number"
                              value={currentForm.inputMarginRight}
                              onChange={(e) => handleInputChange("inputMarginRight", Number(e.target.value))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Bottom</label>
                            <input
                              type="number"
                              value={currentForm.inputMarginBottom}
                              onChange={(e) =>
                                handleInputChange("inputMarginBottom", Number(e.target.value))
                              }
                            />
                          </div>
                          <div className="form-group">
                            <label>Left</label>
                            <input
                              type="number"
                              value={currentForm.inputMarginLeft}
                              onChange={(e) => handleInputChange("inputMarginLeft", Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Fields Configuration */}
                <div className="control-section">
                  <div className="section-header" onClick={() => toggleSection('fields')}>
                    <h3>Form Fields</h3>
                    <span className="toggle-icon">{expandedSections.fields ? '−' : '+'}</span>
                  </div>
                  {expandedSections.fields && (
                    <div className="section-content">
                      <div className="fields-list">
                        {(currentForm.fields || []).map((field, idx) => {
                          const f = normalizeField(field);
                          return (
                            <div key={f.id} className="field-item">
                              <div className="field-header">
                                <span>Field {idx + 1}: {f.name || 'Unnamed'}</span>
                                <button className="btn-icon" onClick={() => removeField(idx)}>✕</button>
                              </div>
                              <div className="grid-2">
                                <div className="form-group">
                                  <label>Label</label>
                                  <input
                                    value={f.name}
                                    onChange={(e) => handleFieldChange(idx, "name", e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Type</label>
                                  <select
                                    value={f.type}
                                    onChange={(e) => handleFieldChange(idx, "type", e.target.value)}
                                  >
                                    {FIELD_TYPES.map((t) => (
                                      <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="form-group">
                                <label>Placeholder</label>
                                <input
                                  value={f.placeholder}
                                  onChange={(e) => handleFieldChange(idx, "placeholder", e.target.value)}
                                />
                              </div>
                              {f.type === "select" && (
                                <div className="form-group">
                                  <label>Options (comma separated)</label>
                                  <input
                                    value={(f.options || []).join(", ")}
                                    onChange={(e) => handleFieldChange(idx, "options", e.target.value.split(",").map(x => x.trim()).filter(Boolean))}
                                  />
                                </div>
                              )}
                              <div className="form-group">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={f.required}
                                    onChange={(e) => handleFieldChange(idx, "required", e.target.checked)}
                                  />
                                  Required Field
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button className="btn btn-block" onClick={addField}>
                        + Add Field
                      </button>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="actions-section">
                  {error && <div className="error-message">{error}</div>}
                  {success && <div className="success-message">{success}</div>}
                  <div className="action-buttons">
                    <button className="btn btn-danger" onClick={deleteCurrentForm}>
                      Delete Form
                    </button>
                    <button className="btn btn-primary" onClick={saveCurrentForm} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Form'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="right-panel">
          {preview}
        </div>
      </div>
    </div>
  );
}
